import { format } from 'date-fns';
import styled from 'styled-components';
import { themeVar } from '@/styles/theme';

import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { CalendarIcon } from '@/assets/icons/info-icons/calendar';
import { EditIcon } from '@/assets/icons/info-icons/edit';
import { User } from '@/types/user';
import { useResponsive } from '../../../hooks/use-responsive';
import { blinkingFrame } from '../../table/styles';
import { NoteButton } from '../noteButton';
import { IChecklistItem, ISection } from './types';
import { DueDiligencePageGetDueDiligenceRecordQuery, DueDiligenceRecordStatus, DueDiligenceRecordTierStatus } from '@/__generated__/graphql';

type SectionStatusProps = {
  currentRow?:
    | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]
    | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number];
  openNotes: (
    note: string,
    updateAt: Date,
    task: string,
    assignedBy:
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['data']['assignedBy']
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number]['data']['assignedBy'],
    assignedTo:
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['data']['assignedTo']
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number]['data']['assignedTo']
  ) => void;
  openModalWindow: (
    type: string,
    row:
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number]
  ) => void;
  sectionTitle: string;
  isSection?: boolean;
  owner: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['owner'];
  prevTierStatus: DueDiligenceRecordTierStatus | null;
  tierStatus?: DueDiligenceRecordTierStatus;
  loading?: boolean;
  recordStatus: DueDiligenceRecordStatus | null;
};

const SectionStatus = ({
  currentRow,
  openNotes,
  openModalWindow,
  sectionTitle,
  isSection = false,
  owner,
  prevTierStatus,
  tierStatus,
  loading = false,
  recordStatus
}: SectionStatusProps) => {
  const { isMobile } = useResponsive();

  if (!currentRow) return <></>;

  const isDisabled =
    tierStatus === DueDiligenceRecordTierStatus.Completed ||
    tierStatus === DueDiligenceRecordTierStatus.Rejected ||
    prevTierStatus === DueDiligenceRecordTierStatus.Rejected ||
    recordStatus === DueDiligenceRecordStatus.Approved ||
    recordStatus === DueDiligenceRecordStatus.Rejected;

  return (
    <>
      {currentRow.data.completed ? (
        <AssignedToWrapper isCompletedMobile={isMobile} loading={loading ? 1 : 0}>
          <ApprovedIcon fill={themeVar('context.success')} width={18} height={18} />
          <AssignedTo isCompletedMobile={isMobile} isMobile={isMobile}>
            Completed By:
            <BoldText>{` ${currentRow.data.completedBy?.firstName} ${currentRow.data.completedBy?.lastName} on ${format(
              new Date(currentRow.data.completedAt),
              'LLL d, yyyy'
            )} `}</BoldText>
          </AssignedTo>
        </AssignedToWrapper>
      ) : (
        <AssignedToWrapper isMobile={isMobile} loading={loading ? 1 : 0}>
          <EditAssignedTo isMobile={isMobile} onClick={!isDisabled ? () => openModalWindow('change-assignee', currentRow) : () => null}>
            <CalendarIcon fill={themeVar('context.warning')} />
            <AssignedTo isMobile={isMobile}>
              Assigned To:{' '}
              <RedirectText disabled={isDisabled}>
                {currentRow.data.assignedTo
                  ? currentRow.data.assignedTo.firstName + ' ' + currentRow.data.assignedTo.lastName
                  : owner?.firstName + ' ' + owner?.lastName}
              </RedirectText>
            </AssignedTo>
            {!isDisabled && <EditIcon width={16} height={16} fill={themeVar('font.action')} style={{ cursor: 'pointer', margin: '0 20px 0 6px' }} />}
          </EditAssignedTo>
          {isMobile && !isDisabled && currentRow.data.note && (
            <NoteButton
              openNotes={() =>
                openNotes(currentRow.data.note, currentRow.data.updatedAt, sectionTitle, currentRow.data.assignedBy, currentRow.data.assignedTo)
              }
            />
          )}
          {!isMobile && !isDisabled && (
            <NoteButton
              isVisible={Boolean(currentRow.data.note)}
              openNotes={() =>
                openNotes(currentRow.data.note, currentRow.data.updatedAt, sectionTitle, currentRow.data.assignedBy, currentRow.data.assignedTo)
              }
            />
          )}
        </AssignedToWrapper>
      )}
    </>
  );
};

export default SectionStatus;

export const AssignedToWrapper = styled.div<{
  isMobile?: boolean;
  isCompletedMobile?: boolean;
  loading?: boolean | number;
}>`
  display: flex;
  gap: ${({ isMobile, isCompletedMobile }) => (isMobile || isCompletedMobile ? '10px' : '0')};
  align-items: ${({ isMobile, isCompletedMobile }) => (isMobile || isCompletedMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};

  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  animation: ${({ loading }) => (loading ? blinkingFrame : null)} 1.5s infinite;
`;

export const EditAssignedTo = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '10px' : '0')};
`;

export const AssignedTo = styled.div<{ isMobile: boolean; isCompletedMobile?: boolean }>`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  padding-left: ${({ isMobile }) => (isMobile ? '0' : '11px')};
  color: ${themeVar('font.base')};
  display: flex;
  flex-direction: ${({ isMobile, isCompletedMobile }) => (isMobile && isCompletedMobile ? 'column' : 'row')};
  gap: 3px;
`;

const BoldText = styled.span`
  font-weight: 700;
`;

export const RedirectText = styled.span<{ disabled: boolean }>`
  color: ${({ disabled }) => (disabled ? themeVar('font.base') : themeVar('font.action'))};
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
`;
